// @ts-strict-ignore
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Radio } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { CustomerEntityType } from 'types/customers'
import { ErrorDetails } from 'types/types'
import {
  ConfirmBusinessCustomerMethod,
  ConfirmCustomerData,
  ConfirmIndividualCustomerMethod,
} from '../helpers/types'
import { AddBusinessCustomer } from './AddBusinessCustomer'
import { AddIndividualCustomer } from './AddIndividualCustomer'

export interface AddCustomerFormProps {
  defaultValues: ConfirmCustomerData
  onConfirm: (customerData: ConfirmCustomerData) => void
  onCancel: () => void
  fieldErrors?: ErrorDetails
  setFieldErrors?: (errors: ErrorDetails) => void
}

export const AddCustomerForm = ({
  defaultValues,
  onConfirm,
  onCancel,
  fieldErrors,
  setFieldErrors,
}: AddCustomerFormProps) => {
  const { t } = useTranslation()
  const { enableAddEmbeddedIndividual } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const {
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
    isPartnerManagedCustomerCompanyCapabilityEnabled,
  } = accountCapabilities || {}
  const { enableRelianceBusinessFields } = useFeatureFlags()
  const isAddBusinessCustomerEnabled =
    isEmbeddedCustomerCompanyCapabilityEnabled || isPartnerManagedCustomerCompanyCapabilityEnabled

  const isRelianceCustomer = useMemo(() => {
    return isPartnerManagedCustomerCompanyCapabilityEnabled && enableRelianceBusinessFields
  }, [isPartnerManagedCustomerCompanyCapabilityEnabled, enableRelianceBusinessFields])

  const [customerType, setCustomerType] = useState<string>(
    defaultValues?.individual || !isAddBusinessCustomerEnabled
      ? CustomerEntityType.INDIVIDUAL
      : CustomerEntityType.COMPANY
  )

  const onConfirmBusinessCustomer: ConfirmBusinessCustomerMethod = businessCustomerData => {
    onConfirm({ business: businessCustomerData })
  }

  const onConfirmIndividualCustomer: ConfirmIndividualCustomerMethod = individualCustomerData => {
    onConfirm({ individual: individualCustomerData })
  }

  const customerTypeOptions = useMemo(
    () => [
      {
        value: CustomerEntityType.COMPANY,
        label: t('customers.business'),
        disabled: !isAddBusinessCustomerEnabled,
      },
      {
        value: CustomerEntityType.INDIVIDUAL,
        label: t('customers.individual'),
        disabled: !isEmbeddedCustomerIndividualCapabilityEnabled || !enableAddEmbeddedIndividual,
      },
    ],
    [
      enableAddEmbeddedIndividual,
      isAddBusinessCustomerEnabled,
      isEmbeddedCustomerIndividualCapabilityEnabled,
      t,
    ]
  )

  return (
    <Box flexCol gap={16}>
      <Radio
        value={customerType}
        onChange={value => {
          setCustomerType(value)
          setFieldErrors(null)
        }}
        horizontal
        options={customerTypeOptions}
      />
      {customerType === CustomerEntityType.COMPANY && (
        <AddBusinessCustomer
          defaultValues={defaultValues?.business?.data}
          onConfirm={onConfirmBusinessCustomer}
          onCancel={onCancel}
          fieldErrors={fieldErrors}
          isReliance={isRelianceCustomer}
        />
      )}
      {customerType === CustomerEntityType.INDIVIDUAL && (
        <AddIndividualCustomer
          defaultValues={defaultValues?.individual?.data}
          onConfirm={onConfirmIndividualCustomer}
          onCancel={onCancel}
          fieldErrors={fieldErrors}
        />
      )}
    </Box>
  )
}
